import { useEffect, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import ConsumerLayout from '../../components/consumer/ConsumerLayout';
import Breadcrumb from '../../components/common/Breadcrumb';
import { fetchConsumerDashboard } from '../../api/consumer';

// Star rating display
const StarRating = ({ rating }) => {
  const full = Math.floor(rating);
  const half = rating % 1 >= 0.5;
  return (
    <span className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <svg key={i} width="13" height="13" viewBox="0 0 24 24"
          fill={i <= full ? '#f59e0b' : (i === full + 1 && half ? 'url(#half)' : 'none')}
          stroke="#f59e0b" strokeWidth="1.5">
          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
        </svg>
      ))}
    </span>
  );
};

// Badge for consumer rank based on review count
const getRankBadge = (totalReviews) => {
  if (totalReviews >= 50) return { label: 'Platinum', color: 'bg-purple-100 text-purple-700' };
  if (totalReviews >= 20) return { label: 'Gold', color: 'bg-amber-100 text-amber-700' };
  if (totalReviews >= 10) return { label: 'Silver', color: 'bg-slate-100 text-slate-700' };
  if (totalReviews >= 1)  return { label: 'Bronze', color: 'bg-orange-100 text-orange-700' };
  return { label: 'New Member', color: 'bg-emerald-100 text-emerald-700' };
};

const formatDate = (dateStr) => {
  if (!dateStr) return '–';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const timeAgo = (dateStr) => {
  if (!dateStr) return '–';
  const diff = Date.now() - new Date(dateStr).getTime();
  const days = Math.floor(diff / 86400000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 30) return `${days} days ago`;
  if (days < 365) return `${Math.floor(days / 30)} months ago`;
  return `${Math.floor(days / 365)} years ago`;
};

// Single stat card
const StatCard = ({ icon, label, value, sub, color }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-5 flex items-start gap-4">
    <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${color}`}>
      {icon}
    </div>
    <div className="min-w-0">
      <p className="text-[12px] font-medium text-slate-500 uppercase tracking-wide leading-none mb-1">{label}</p>
      <p className="text-2xl font-bold text-slate-900 leading-none">{value}</p>
      {sub && <p className="text-[11px] text-slate-400 mt-1">{sub}</p>}
    </div>
  </div>
);

const ConsumerDashboard = () => {
  const { user } = useAuth();
  const [dashData, setDashData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const data = await fetchConsumerDashboard();
        setDashData(data);
      } catch (err) {
        setError(err?.response?.data?.message || 'Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const stats = dashData?.stats;
  const profile = dashData?.user?.profile_consumer?.[0] ?? dashData?.user?.profile_consumer ?? {};
  const recentReviews = dashData?.recentReviews ?? [];
  const rank = getRankBadge(stats?.totalReviews ?? 0);

  return (
    <ConsumerLayout title="Dashboard">
      <Breadcrumb items={[{ label: 'Dashboard', href: '/consumer/dashboard' }]} />

      {/* Welcome Banner */}
      <div className="mb-6 rounded-xl border border-emerald-100 bg-gradient-to-r from-emerald-50 to-teal-50 px-6 py-5 flex items-center justify-between">
        <div>
          <h2 className="text-[17px] font-semibold text-slate-800">
            Welcome back, {user?.name?.split(' ')[0]} 👋
          </h2>
          <p className="text-[13px] text-slate-500 mt-0.5">
            {user?.email} &nbsp;·&nbsp; Member since {formatDate(user?.created_at ?? dashData?.user?.created_at)}
          </p>
        </div>
        {/* <span className={`rounded-full px-3 py-1 text-[12px] font-semibold ${rank.color}`}>
          {rank.label}
        </span> */}
      </div>

      {/* Loading / Error States */}
      {loading && (
        <div className="flex items-center justify-center py-24">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-500"></div>
        </div>
      )}

      {!loading && error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 text-[13px]">
          {error}
        </div>
      )}

      {!loading && !error && dashData && (
        <>
          {/* Stats Cards */}
          <div className="grid grid-cols-3 gap-4 mb-6 w-fit mx-auto">
            <StatCard
              icon={
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.75">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="16" y1="13" x2="8" y2="13" />
                  <line x1="16" y1="17" x2="8" y2="17" />
                  <polyline points="10 9 9 9 8 9" />
                </svg>
              }
              label="Total Reviews"
              value={stats.totalReviews}
              sub={`${stats.approvedReviews} approved`}
              color="bg-blue-500"
            />
            {/* <StatCard
              icon={
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.75">
                  <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3z" />
                  <path d="M7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3" />
                </svg>
              }
              label="Helpful Votes"
              value={stats.totalHelpful}
              sub="People found your reviews helpful"
              color="bg-emerald-500"
            /> */}
            <StatCard
              icon={
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.75">
                  <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                </svg>
              }
              label="Avg Rating Given"
              value={stats.avgRating > 0 ? `${stats.avgRating} ★` : '–'}
              sub="Across all your reviews"
              color="bg-amber-500"
            />
            <StatCard
              icon={
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.75">
                  <rect x="2" y="7" width="20" height="14" rx="2" />
                  <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
                </svg>
              }
              label="Businesses Reviewed"
              value={stats.uniqueBusinesses}
              sub="Unique businesses"
              color="bg-violet-500"
            />
          </div>

          {/* Rating Distribution */}
          {stats.totalReviews > 0 && (
            <div className="bg-white rounded-xl border border-slate-200 p-5 mb-6">
              <h3 className="text-[13px] font-semibold text-slate-800 mb-4">Your Rating Distribution</h3>
              <div className="space-y-2">
                {[5, 4, 3, 2, 1].map((star) => {
                  const count = stats.ratingDistribution?.[star] ?? 0;
                  const pct = stats.totalReviews > 0 ? Math.round((count / stats.totalReviews) * 100) : 0;
                  return (
                    <div key={star} className="flex items-center gap-3">
                      <span className="text-[12px] text-slate-500 w-6 text-right">{star}★</span>
                      <div className="flex-1 bg-slate-100 rounded-full h-2">
                        <div
                          className="bg-amber-400 h-2 rounded-full transition-all duration-500"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <span className="text-[12px] text-slate-400 w-8">{count}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Recent Reviews */}
          <div className="bg-white rounded-xl border border-slate-200">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
              <h3 className="text-[13px] font-semibold text-slate-800">Recent Reviews</h3>
              <span className="text-[11px] text-slate-400">Last 5 reviews</span>
            </div>

            {recentReviews.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-14 text-slate-400">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.25" className="mb-3 text-slate-300">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                </svg>
                <p className="text-[13px] font-medium">No reviews yet</p>
                <p className="text-[12px] mt-1">Start exploring businesses and share your experience</p>
                <a
                  href="/businesses"
                  className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-emerald-500 px-4 py-2 text-[12px] font-semibold text-white hover:bg-emerald-600 transition-colors"
                >
                  Browse Businesses
                </a>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-[13px]">
                  <thead>
                    <tr className="border-b border-slate-100">
                      <th className="px-5 py-3 text-left text-[11px] font-semibold text-slate-400 uppercase tracking-wide">Business</th>
                      <th className="px-5 py-3 text-left text-[11px] font-semibold text-slate-400 uppercase tracking-wide">Title</th>
                      <th className="px-5 py-3 text-left text-[11px] font-semibold text-slate-400 uppercase tracking-wide">Rating</th>
                      <th className="px-5 py-3 text-left text-[11px] font-semibold text-slate-400 uppercase tracking-wide">Helpful</th>
                      <th className="px-5 py-3 text-left text-[11px] font-semibold text-slate-400 uppercase tracking-wide">Posted</th>
                      <th className="px-5 py-3 text-left text-[11px] font-semibold text-slate-400 uppercase tracking-wide">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentReviews.map((review, idx) => (
                      <tr key={review.id} className={`border-b border-slate-50 hover:bg-slate-50 transition-colors ${idx === recentReviews.length - 1 ? 'border-b-0' : ''}`}>
                        <td className="px-5 py-3.5">
                          <span className="font-medium text-slate-800">
                            {review.business?.business_name ?? 'Unknown Business'}
                          </span>
                          {review.business?.city && (
                            <span className="text-slate-400"> · {review.business.city}</span>
                          )}
                        </td>
                        <td className="px-5 py-3.5 max-w-[200px]">
                          <span className="text-slate-600 truncate block" title={review.title}>
                            {review.title || '–'}
                          </span>
                        </td>
                        <td className="px-5 py-3.5">
                          <div className="flex items-center gap-1.5">
                            <StarRating rating={review.rating} />
                            <span className="text-slate-500">{review.rating}</span>
                          </div>
                        </td>
                        <td className="px-5 py-3.5">
                          <span className="text-emerald-600 font-medium">{review.helpful_count ?? 0}</span>
                        </td>
                        <td className="px-5 py-3.5 text-slate-500 whitespace-nowrap">
                          {timeAgo(review.created_at)}
                        </td>
                        <td className="px-5 py-3.5">
                          {review.is_approved ? (
                            <span className="inline-flex items-center rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-medium text-emerald-700">
                              Approved
                            </span>
                          ) : (
                            <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-700">
                              Pending
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </ConsumerLayout>
  );
};

export default ConsumerDashboard;
